import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Flex,
  Icon,
  Link,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { FaHome, FaPlay, FaInfoCircle, FaEnvelope } from 'react-icons/fa';
import SEO from '../components/SEO';

const quickLinks = [
  { to: "/episodes", text: "Latest Episodes", icon: FaPlay },
  { to: "/about", text: "About Us", icon: FaInfoCircle },
  { to: "/contact", text: "Contact Us", icon: FaEnvelope },
];

const NotFound = () => {
  const location = useLocation();

  return (
    <>
      <SEO
        title="404 - Page Not Found | Namaskar India"
        description="The page you're looking for doesn't exist."
        noindex={true}
      />

      <Box minH="100vh" py={{ base: 20, md: 24 }} position="relative" overflow="hidden">
        {/* Ambient orbs */}
        <Box
          position="absolute"
          top="10%"
          left="-5%"
          w="400px"
          h="400px"
          borderRadius="full"
          bg="rgba(255,152,0,0.08)"
          filter="blur(80px)"
          pointerEvents="none"
        />
        <Box
          position="absolute"
          bottom="15%"
          right="-5%"
          w="350px"
          h="350px"
          borderRadius="full"
          bg="rgba(160,60,20,0.08)"
          filter="blur(80px)"
          pointerEvents="none"
        />

        <Container maxW="container.xl" position="relative">
          <VStack spacing={8} textAlign="center">
            {/* Big glowing 404 */}
            <Box position="relative">
              <Text
                fontSize={{ base: "8xl", md: "9xl" }}
                fontFamily="heading"
                bgGradient="linear(to-r, brand.400, terracotta.400)"
                bgClip="text"
                lineHeight="1"
                style={{
                  filter: 'drop-shadow(0 0 40px rgba(255,152,0,0.35))'
                }}
              >
                404
              </Text>
            </Box>

            <Heading
              as="h1"
              fontSize={{ base: "3xl", sm: "4xl", md: "6xl", lg: "7xl" }}
              variant="gradient"
            >
              Page Not Found
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="whiteAlpha.700"
              maxW="xl"
              px={{ base: 4, md: 0 }}
            >
              We couldn't find{' '}
              <Text as="span" color="brand.400" fontWeight="medium">
                {location.pathname}
              </Text>
              . Here are some helpful links to get you back on track:
            </Text>

            <VStack spacing={5} width="100%" maxW="md">
              <Button
                as={RouterLink}
                to="/"
                colorScheme="brand"
                size="lg"
                width="full"
                leftIcon={<Icon as={FaHome} boxSize={5} />}
                boxShadow="0 8px 30px rgba(255,152,0,0.3)"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(255,152,0,0.45)',
                }}
                transition="all 0.25s"
              >
                Return Home
              </Button>

              <Flex
                direction={{ base: 'column', sm: 'row' }}
                gap={3}
                justify="center"
                pt={2}
                w="full"
              >
                {quickLinks.map((link) => (
                  <Link
                    key={link.to}
                    as={RouterLink}
                    to={link.to}
                    _hover={{ textDecoration: 'none' }}
                  >
                    <HStack
                      spacing={2}
                      background="rgba(255,150,50,0.04)"
                      backdropFilter="blur(12px)"
                      border="1px solid rgba(255,150,50,0.08)"
                      p={3}
                      borderRadius="lg"
                      transition="all 0.25s"
                      justify="center"
                      _hover={{
                        borderColor: 'rgba(255,152,0,0.35)',
                        background: 'rgba(255,152,0,0.06)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(255,152,0,0.12)',
                      }}
                    >
                      <Icon as={link.icon} color="brand.400" boxSize={4} />
                      <Text color="whiteAlpha.800" fontSize="sm" fontWeight="medium">
                        {link.text}
                      </Text>
                    </HStack>
                  </Link>
                ))}
              </Flex>
            </VStack>
          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default NotFound;
